import type { ReactNode } from "react";

export function Card({
  children,
  className = "",
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={`rounded border border-line bg-surface ${className}`}>
      {children}
    </div>
  );
}

export function Label({ children }: { children: ReactNode }) {
  return (
    <span className="font-mono text-[11px] tracking-[0.12em] text-ink-3 uppercase">
      {children}
    </span>
  );
}

interface FieldProps {
  label: string;
  error?: string;
  hint?: string;
  children: ReactNode;
}

export function Field({ label, error, hint, children }: FieldProps) {
  return (
    <label className="flex flex-col gap-1.5">
      <Label>{label}</Label>
      {children}
      {error ? (
        <span className="text-sm text-danger">{error}</span>
      ) : hint ? (
        <span className="text-sm text-ink-3">{hint}</span>
      ) : null}
    </label>
  );
}

export const inputClass =
  "w-full rounded border border-line bg-ground px-3 py-2.5 text-ink " +
  "placeholder:text-ink-3 focus:border-signal focus:outline-none " +
  "focus:ring-1 focus:ring-signal disabled:opacity-50";

export function Button({
  children,
  variant = "primary",
  className = "",
  ...rest
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "ghost" | "danger";
}) {
  const styles = {
    primary: "bg-signal text-ground hover:brightness-110 font-medium",
    ghost: "border border-line text-ink-2 hover:text-ink hover:border-ink-3",
    danger: "border border-danger text-danger hover:bg-danger hover:text-ground",
  }[variant];

  return (
    <button
      {...rest}
      className={`rounded px-4 py-2.5 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-signal disabled:cursor-not-allowed disabled:opacity-50 ${styles} ${className}`}
    >
      {children}
    </button>
  );
}

export function Alert({ children }: { children: ReactNode }) {
  return (
    <div className="rounded border border-danger/40 bg-danger/10 px-3 py-2.5 text-sm text-danger">
      {children}
    </div>
  );
}

export function Spinner({ label = "Loading" }: { label?: string }) {
  return (
    <div className="flex items-center gap-2 text-ink-3">
      <span
        aria-hidden
        className="size-3.5 animate-spin rounded-full border-2 border-line border-t-signal motion-reduce:animate-none"
      />
      <span className="text-sm">{label}</span>
    </div>
  );
}
